import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Crown, Users, ArrowUpRight, History, Settings, Search, Volume2, Play, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_BADGE = {
  WAITLIST: 'bg-slate-200 text-slate-800 border-slate-300',
  ROOKIE: 'bg-blue-100 text-blue-800 border-blue-300',
  PRO: 'bg-purple-100 text-purple-800 border-purple-300',
  ENTERPRISE: 'bg-amber-100 text-amber-800 border-amber-300',
  ADMIN: 'bg-red-100 text-red-800 border-red-300',
};

const TIERS = ['WAITLIST', 'ROOKIE', 'PRO', 'ENTERPRISE'];

const AdminTierManagement = () => {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [users, setUsers] = useState([]);
  const [filterTier, setFilterTier] = useState('ALL');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [newTier, setNewTier] = useState('ROOKIE');
  const [note, setNote] = useState('');
  const [audit, setAudit] = useState([]);
  const [bundles, setBundles] = useState(null);
  const [bundlesDraft, setBundlesDraft] = useState('');
  const [audioConfig, setAudioConfig] = useState(null);
  const [audioConfigDraft, setAudioConfigDraft] = useState('');
  const [previewErrors, setPreviewErrors] = useState({});
  const [previewingKey, setPreviewingKey] = useState(null);

  // Lightweight, low-risk audio preview. Builds a fresh <Audio> per click so
  // failures on one preset never break the page or block other clips.
  const previewPreset = (key, rawUrl) => {
    setPreviewErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (!rawUrl) {
      setPreviewErrors((prev) => ({ ...prev, [key]: 'Audio preview failed. Check file path.' }));
      return;
    }
    const url = rawUrl.startsWith('http')
      ? rawUrl
      : `${BACKEND_URL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
    try {
      const a = new Audio(url);
      a.volume = typeof audioConfig?.default_volume === 'number' ? audioConfig.default_volume : 0.5;
      setPreviewingKey(key);
      a.onended = () => setPreviewingKey((k) => (k === key ? null : k));
      a.onerror = () => {
        setPreviewingKey((k) => (k === key ? null : k));
        setPreviewErrors((prev) => ({ ...prev, [key]: 'Audio preview failed. Check file path.' }));
      };
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          setPreviewingKey((k) => (k === key ? null : k));
          setPreviewErrors((prev) => ({ ...prev, [key]: 'Audio preview failed. Check file path.' }));
        });
      }
    } catch (_) {
      setPreviewingKey(null);
      setPreviewErrors((prev) => ({ ...prev, [key]: 'Audio preview failed. Check file path.' }));
    }
  };

  const loadAudioConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/audio-notifications/config`, { headers: auth });
      setAudioConfig(res.data.config);
      setAudioConfigDraft(JSON.stringify(res.data.config, null, 2));
    } catch (e) {
      toast.error('Failed to load audio config');
    }
  };

  const saveAudioConfig = async () => {
    try {
      const parsed = JSON.parse(audioConfigDraft);
      await axios.put(`${API}/admin/audio-notifications/config`, parsed, { headers: auth });
      toast.success('Audio notification config saved.');
      loadAudioConfig();
    } catch (e) {
      toast.error('Invalid JSON or save failed');
    }
  };

  const toggleGlobalAudio = async () => {
    try {
      await axios.put(
        `${API}/admin/audio-notifications/config`,
        { enabled_globally: !audioConfig?.enabled_globally },
        { headers: auth },
      );
      loadAudioConfig();
      toast.success(audioConfig?.enabled_globally ? 'Global audio disabled' : 'Global audio enabled');
    } catch (e) {
      toast.error('Failed to toggle');
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterTier && filterTier !== 'ALL') params.tier = filterTier;
      if (search) params.search = search;
      const res = await axios.get(`${API}/admin/tiers/users`, { headers: auth, params });
      setUsers(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async () => {
    try {
      const res = await axios.get(`${API}/admin/tiers/audit?limit=100`, { headers: auth });
      setAudit(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load audit log');
    }
  };

  const loadBundles = async () => {
    try {
      const res = await axios.get(`${API}/admin/credits/starter-bundles`, { headers: auth });
      setBundles(res.data.bundles);
      setBundlesDraft(JSON.stringify(res.data.bundles, null, 2));
    } catch (e) {
      toast.error('Failed to load starter bundles');
    }
  };

  useEffect(() => {
    loadUsers();
    loadAudit();
    loadBundles();
    loadAudioConfig();
  }, []);

  const handleChangeTier = async () => {
    if (!editUser) return;
    try {
      const res = await axios.post(
        `${API}/admin/tiers/users/${editUser.id}/tier`,
        { new_tier: newTier, note: note || undefined },
        { headers: auth },
      );
      if (res.data.ok) {
        toast.success(
          res.data.changed
            ? `Tier updated: ${res.data.from_tier} → ${res.data.to_tier}`
            : 'Tier unchanged (same value).'
        );
        setEditUser(null);
        setNote('');
        loadUsers();
        loadAudit();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update tier');
    }
  };

  const saveBundles = async () => {
    try {
      const parsed = JSON.parse(bundlesDraft);
      const res = await axios.put(`${API}/admin/credits/starter-bundles`, parsed, { headers: auth });
      if (res.data.ok) {
        toast.success('Starter bundles saved.');
        loadBundles();
      }
    } catch (e) {
      toast.error('Invalid JSON or save failed');
    }
  };

  return (
    <AdminLayout title="Tier Management">
      <div className="space-y-6" data-testid="admin-tier-management">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-600" /> Tier System (P1)
            </CardTitle>
            <CardDescription>
              Strict ladder: WAITLIST &lt; ROOKIE &lt; PRO &lt; ENTERPRISE. ADMIN is separate.
              Promoting to a new tier grants its one-time starter credit bundle (idempotent).
            </CardDescription>
          </CardHeader>
        </Card>

        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="h-4 w-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <History className="h-4 w-4 mr-2" />Audit Log
            </TabsTrigger>
            <TabsTrigger value="bundles" data-testid="tab-bundles">
              <Settings className="h-4 w-4 mr-2" />Starter Bundles
            </TabsTrigger>
            <TabsTrigger value="audio" data-testid="tab-audio">
              <Volume2 className="h-4 w-4 mr-2" />Audio Notifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="flex-1 flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search email or name…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      data-testid="user-search-input"
                    />
                  </div>
                  <Select value={filterTier} onValueChange={setFilterTier}>
                    <SelectTrigger className="w-[200px]" data-testid="filter-tier-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All tiers</SelectItem>
                      {TIERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      <SelectItem value="ADMIN">ADMIN</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadUsers} data-testid="apply-filters-btn">Apply</Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="py-10 text-center text-muted-foreground">Loading…</div>
                ) : users.length === 0 ? (
                  <div className="py-10 text-center text-muted-foreground">No users found.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tier-users-table">
                      <thead>
                        <tr className="text-left border-b">
                          <th className="py-2">Email</th>
                          <th className="py-2">Name</th>
                          <th className="py-2">Role</th>
                          <th className="py-2">Effective Tier</th>
                          <th className="py-2">Stored Tier</th>
                          <th className="py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} className="border-b" data-testid={`tier-user-row-${u.id}`}>
                            <td className="py-2">{u.email}</td>
                            <td className="py-2">{u.full_name}</td>
                            <td className="py-2 capitalize">{u.role}</td>
                            <td className="py-2">
                              <Badge className={TIER_BADGE[u.effective_tier] || ''}>
                                {u.effective_tier}
                              </Badge>
                            </td>
                            <td className="py-2 text-muted-foreground">
                              {u.user_tier || u.membership_tier || '—'}
                            </td>
                            <td className="py-2 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={u.effective_tier === 'ADMIN'}
                                onClick={() => {
                                  setEditUser(u);
                                  setNewTier(u.effective_tier === 'ADMIN' ? 'ROOKIE' : u.effective_tier);
                                }}
                                data-testid={`change-tier-btn-${u.id}`}
                              >
                                <ArrowUpRight className="h-3 w-3 mr-1" /> Change Tier
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-4 w-4" /> Tier Change Audit
                </CardTitle>
                <CardDescription>Last 100 tier changes across all users.</CardDescription>
              </CardHeader>
              <CardContent>
                {audit.length === 0 ? (
                  <div className="py-6 text-center text-muted-foreground">No audit entries yet.</div>
                ) : (
                  <div className="space-y-2" data-testid="tier-audit-list">
                    {audit.map((a) => (
                      <div key={a.id} className="border rounded p-3 flex items-center justify-between text-sm">
                        <div>
                          <div className="font-medium">User <code>{a.user_id.slice(0, 8)}…</code></div>
                          <div className="text-muted-foreground">
                            {a.from_tier || '—'} → <b>{a.to_tier}</b>
                            <span className="ml-2 text-xs">({a.reason})</span>
                          </div>
                          {a.note && <div className="text-xs text-muted-foreground mt-1">“{a.note}”</div>}
                        </div>
                        <div className="text-xs text-muted-foreground text-right">
                          <div>{new Date(a.created_at).toLocaleString()}</div>
                          {a.actor_user_id ? <div>by {a.actor_user_id.slice(0, 8)}…</div> : <div>system</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bundles">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-4 w-4" /> Tier Starter Credit Bundles
                </CardTitle>
                <CardDescription>
                  One-time credit grant applied when a user enters the tier. Edit JSON below.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Label>Bundles JSON</Label>
                <textarea
                  className="w-full font-mono text-xs border rounded p-3 mt-1"
                  rows={16}
                  value={bundlesDraft}
                  onChange={(e) => setBundlesDraft(e.target.value)}
                  data-testid="bundles-json-textarea"
                />
                <div className="flex gap-2 mt-3">
                  <Button onClick={saveBundles} data-testid="save-bundles-btn">Save Bundles</Button>
                  <Button variant="outline" onClick={loadBundles}>Reset</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audio">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4" /> Audio Notification Settings
                </CardTitle>
                <CardDescription>
                  Global on/off plus per-event audio preset mapping. Members can mute individually from their notifications page.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between border rounded p-3 mb-4">
                  <div>
                    <div className="font-medium text-sm">Globally enabled</div>
                    <div className="text-xs text-muted-foreground">
                      When off, no member hears notification sounds regardless of personal preferences.
                    </div>
                  </div>
                  <Button
                    onClick={toggleGlobalAudio}
                    variant={audioConfig?.enabled_globally ? 'default' : 'outline'}
                    data-testid="toggle-global-audio-btn"
                  >
                    {audioConfig?.enabled_globally ? 'ON' : 'OFF'}
                  </Button>
                </div>

                <Label>Preset preview</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Click ▶ Preview to hear each branded clip. Skips the silent <code>off</code> preset.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5" data-testid="audio-preset-preview-grid">
                  {audioConfig && Object.entries(audioConfig.presets || {})
                    .filter(([key, url]) => key !== 'off' && url)
                    .map(([key, url]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between border rounded px-3 py-2 text-xs"
                        data-testid={`audio-preset-row-${key}`}
                      >
                        <div className="min-w-0 mr-3">
                          <div className="font-medium text-slate-800 truncate" title={key}>{key}</div>
                          <div className="text-[10px] text-muted-foreground truncate" title={url}>{url}</div>
                          {previewErrors[key] && (
                            <div
                              className="text-[10px] text-amber-700 flex items-center gap-1 mt-0.5"
                              data-testid={`audio-preset-error-${key}`}
                            >
                              <AlertTriangle className="h-3 w-3" /> {previewErrors[key]}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => previewPreset(key, url)}
                          data-testid={`audio-preset-preview-btn-${key}`}
                          aria-label={`Preview ${key} audio clip`}
                          className="shrink-0"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          {previewingKey === key ? 'Playing…' : 'Preview'}
                        </Button>
                      </div>
                    ))}
                </div>

                <Label>Full audio config JSON</Label>
                <textarea
                  className="w-full font-mono text-xs border rounded p-3 mt-1"
                  rows={20}
                  value={audioConfigDraft}
                  onChange={(e) => setAudioConfigDraft(e.target.value)}
                  data-testid="audio-config-json-textarea"
                />
                <div className="flex gap-2 mt-3">
                  <Button onClick={saveAudioConfig} data-testid="save-audio-config-btn">Save Audio Config</Button>
                  <Button variant="outline" onClick={loadAudioConfig}>Reset</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Tier</DialogTitle>
              <DialogDescription>
                Update the user's tier. Promotions grant a one-time starter credit bundle (idempotent) and send a tier_changed notification.
              </DialogDescription>
            </DialogHeader>
            {editUser && (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  User: <b>{editUser.email}</b>
                </div>
                <div>
                  <Label>New Tier</Label>
                  <Select value={newTier} onValueChange={setNewTier}>
                    <SelectTrigger data-testid="new-tier-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIERS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for change…" data-testid="tier-change-note" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
                  <Button onClick={handleChangeTier} data-testid="confirm-change-tier-btn">Confirm</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminTierManagement;
